import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2, Package, Factory, PackageCheck, Truck, Printer,
  ClipboardCheck, Camera, CheckCircle, LogIn, X
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const statusColors = {
  draft: 'bg-gray-600', sent: 'bg-blue-600', pending: 'bg-blue-600',
  confirmed: 'bg-cyan-600', manufacturing: 'bg-orange-600', ready: 'bg-purple-600',
  in_transit: 'bg-indigo-600', delivered: 'bg-green-700',
  partially_received: 'bg-amber-600', received: 'bg-green-600',
  completed: 'bg-green-600', cancelled: 'bg-red-600',
};

// Mobile-first PO action page opened by scanning a label QR.
// Staff (logged-in users): quick GRN receiving. Vendors: status updates.
export default function PoScanPage() {
  const { poId } = useParams();
  const navigate = useNavigate();
  const staffToken = localStorage.getItem('token');
  const vendorSession = (() => {
    try { return JSON.parse(localStorage.getItem('vendor_session') || 'null'); } catch { return null; }
  })();
  const mode = staffToken ? 'staff' : vendorSession?.session_token ? 'vendor' : 'anon';

  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acting, setActing] = useState(false);
  // staff GRN state
  const [grnQty, setGrnQty] = useState({});
  const [grnDamaged, setGrnDamaged] = useState({});
  const [grnImages, setGrnImages] = useState([]);
  const [uploadingImg, setUploadingImg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (mode === 'staff') {
        const res = await axios.get(`${API_BASE_URL}/enterprise/purchase-orders/${poId}/grn`, {
          headers: { Authorization: `Bearer ${staffToken}` }
        });
        setPo(res.data.po);
      } else if (mode === 'vendor') {
        const res = await axios.get(`${API_BASE_URL}/enterprise/vendor-portal/po/${poId}`, {
          headers: { 'X-Vendor-Session': vendorSession.session_token }
        });
        setPo(res.data.po);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load purchase order');
    } finally {
      setLoading(false);
    }
  }, [poId, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (mode !== 'anon') load(); else setLoading(false); }, [load, mode]);

  const vendorAction = async (path, successMsg) => {
    setActing(true);
    try {
      await axios.post(`${API_BASE_URL}/enterprise/vendor-portal/po/${poId}/${path}`, {}, {
        headers: { 'X-Vendor-Session': vendorSession.session_token }
      });
      toast.success(successMsg);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const uploadGrnImage = async (file) => {
    if (!file) return;
    setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API_BASE_URL}/enterprise/purchase-orders/${poId}/grn/received-image`, fd, {
        headers: { Authorization: `Bearer ${staffToken}` }
      });
      setGrnImages((p) => [...p, res.data.file_url]);
      toast.success('Photo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingImg(false);
    }
  };

  const submitGrn = async () => {
    const lineItems = (po.line_items || []).map((l) => ({
      line_id: l.id,
      sku: l.sku,
      received_quantity: Math.max(0, parseInt(grnQty[l.id], 10) || 0),
      damaged_quantity: Math.max(0, parseInt(grnDamaged[l.id], 10) || 0),
    })).filter((l) => l.received_quantity > 0 || l.damaged_quantity > 0);
    if (lineItems.length === 0) { toast.error('Enter received quantities first'); return; }
    if (grnImages.length === 0 && (po.grn_received_images || []).length === 0) {
      toast.error('Please add at least one photo of the received goods');
      return;
    }
    setActing(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      await axios.post(`${API_BASE_URL}/enterprise/purchase-orders/${poId}/grn`, {
        line_items: lineItems,
        received_images: grnImages,
        verified_by: user.id,
        verified_by_name: user.full_name,
        notes: 'Received via mobile QR scan',
      }, { headers: { Authorization: `Bearer ${staffToken}` } });
      toast.success('GRN submitted — goods received');
      setGrnQty({}); setGrnDamaged({}); setGrnImages([]);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'GRN failed');
    } finally {
      setActing(false);
    }
  };

  if (mode === 'anon') {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex flex-col items-center justify-center p-6 gap-4">
        <Package className="w-12 h-12 text-amber-400" />
        <h1 className="text-xl font-bold text-center">Purchase Order Scan</h1>
        <p className="text-sm text-gray-400 text-center">Log in to update or receive this PO, then scan the label again.</p>
        <button onClick={() => navigate('/login')} className="w-full max-w-xs py-3 rounded-xl bg-amber-500 text-black font-bold flex items-center justify-center gap-2" data-testid="poscan-staff-login-btn">
          <LogIn className="w-5 h-5" /> Warehouse / Staff Login
        </button>
        <button onClick={() => navigate('/vendor-portal')} className="w-full max-w-xs py-3 rounded-xl border-2 border-amber-500/50 text-amber-400 font-bold flex items-center justify-center gap-2" data-testid="poscan-vendor-login-btn">
          <Factory className="w-5 h-5" /> Vendor Login
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="min-h-screen bg-gray-900 flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-amber-400" /></div>;
  }
  if (error || !po) {
    return <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center p-6"><p className="text-red-400 text-center" data-testid="poscan-error">{error || 'Not found'}</p></div>;
  }

  const status = po.status;
  const allReceived = (po.line_items || []).length > 0 && (po.line_items || []).every(
    (l) => Math.max(0, (l.quantity || 0) - (l.received_quantity || 0) - (l.damaged_quantity || 0)) === 0
  );
  const canReceive = mode === 'staff' && !allReceived && ['sent', 'confirmed', 'manufacturing', 'ready', 'in_transit', 'delivered', 'partially_received'].includes(status);

  return (
    <div className="min-h-screen bg-gray-900 text-white pb-10" data-testid="po-scan-page">
      <div className="p-4 max-w-lg mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-1">
          <h1 className="text-xl font-bold">{po.po_number}</h1>
          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusColors[status] || 'bg-gray-600'}`} data-testid="poscan-status-badge">
            {String(status || '').replace(/_/g, ' ')}
          </span>
        </div>
        <p className="text-sm text-gray-400 mb-4">{po.vendor_name} · {String(po.created_at || '').slice(0, 10)}</p>

        {/* Items */}
        <div className="space-y-2 mb-5">
          {(po.line_items || []).map((l) => {
            const remaining = Math.max(0, (l.quantity || 0) - (l.received_quantity || 0) - (l.damaged_quantity || 0));
            return (
              <div key={l.id} className="bg-gray-800 rounded-xl p-3" data-testid={`poscan-item-${l.sku}`}>
                <p className="text-sm font-medium">{l.product_title}</p>
                <p className="text-xs text-gray-400 mb-1">{l.sku} · Ordered {l.quantity} · Received {l.received_quantity || 0}{(l.damaged_quantity || 0) > 0 ? ` · Damaged ${l.damaged_quantity}` : ''}</p>
                {canReceive && remaining > 0 && (
                  <div className="flex items-center gap-2 mt-2">
                    <label className="text-xs text-gray-400 w-16">Receive:</label>
                    <input type="number" min="0" max={remaining} inputMode="numeric" placeholder="0"
                      value={grnQty[l.id] ?? ''} onChange={(e) => setGrnQty({ ...grnQty, [l.id]: e.target.value })}
                      className="w-20 p-2 rounded-lg bg-gray-900 border border-gray-700 text-white text-base"
                      data-testid={`poscan-receive-input-${l.sku}`} />
                    <label className="text-xs text-gray-400">Damaged:</label>
                    <input type="number" min="0" max={remaining} inputMode="numeric" placeholder="0"
                      value={grnDamaged[l.id] ?? ''} onChange={(e) => setGrnDamaged({ ...grnDamaged, [l.id]: e.target.value })}
                      className="w-20 p-2 rounded-lg bg-gray-900 border border-gray-700 text-white text-base"
                      data-testid={`poscan-damaged-input-${l.sku}`} />
                  </div>
                )}
                {canReceive && remaining === 0 && (
                  <p className="text-xs text-green-400 flex items-center gap-1 mt-1"><CheckCircle className="w-3 h-3" /> Fully received</p>
                )}
              </div>
            );
          })}
        </div>

        {/* VENDOR ACTIONS */}
        {mode === 'vendor' && (
          <div className="space-y-3">
            {['sent', 'pending', 'draft'].includes(status) && (
              <button onClick={() => vendorAction('accept', 'Purchase order accepted')} disabled={acting}
                className="w-full py-4 rounded-xl bg-cyan-600 font-bold text-base flex items-center justify-center gap-2" data-testid="poscan-accept-btn">
                {acting ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle className="w-5 h-5" />} Accept Order
              </button>
            )}
            {['sent', 'pending', 'confirmed'].includes(status) && (
              <button onClick={() => vendorAction('start-manufacturing', 'Manufacturing started')} disabled={acting}
                className="w-full py-4 rounded-xl bg-orange-600 font-bold text-base flex items-center justify-center gap-2" data-testid="poscan-start-mfg-btn">
                {acting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Factory className="w-5 h-5" />} Start Manufacturing
              </button>
            )}
            {status === 'manufacturing' && (
              <button onClick={() => vendorAction('mark-ready', 'Marked as ready')} disabled={acting}
                className="w-full py-4 rounded-xl bg-purple-600 font-bold text-base flex items-center justify-center gap-2" data-testid="poscan-mark-ready-btn">
                {acting ? <Loader2 className="w-5 h-5 animate-spin" /> : <PackageCheck className="w-5 h-5" />} Mark as Ready
              </button>
            )}
            {status === 'ready' && po.fulfillment_method === 'vendor_delivers' && (
              <button onClick={() => vendorAction('mark-delivered', 'Marked as delivered')} disabled={acting}
                className="w-full py-4 rounded-xl bg-green-600 font-bold text-base flex items-center justify-center gap-2" data-testid="poscan-mark-delivered-btn">
                {acting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Truck className="w-5 h-5" />} Mark as Delivered
              </button>
            )}
            {!['draft', 'cancelled'].includes(status) && (
              <button onClick={() => navigate(`/vendor-portal/labels/${poId}`)}
                className="w-full py-3 rounded-xl border-2 border-amber-500/50 text-amber-400 font-semibold flex items-center justify-center gap-2" data-testid="poscan-vendor-print-labels-btn">
                <Printer className="w-5 h-5" /> Print Labels
              </button>
            )}
          </div>
        )}

        {/* STAFF GRN */}
        {mode === 'staff' && (
          <div className="space-y-3">
            {canReceive ? (
              <>
                <div className="bg-gray-800 rounded-xl p-3">
                  <p className="text-sm font-semibold mb-2 flex items-center gap-2"><Camera className="w-4 h-4 text-amber-400" /> Photo of received goods (required)</p>
                  <div className="flex items-center gap-2 flex-wrap">
                    {grnImages.map((u, i) => (
                      <div key={i} className="relative">
                        <img src={u} alt="grn" className="w-14 h-14 rounded-lg object-cover border border-gray-700" />
                        <button onClick={() => setGrnImages(grnImages.filter((_, k) => k !== i))} className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                    <label className="w-14 h-14 rounded-lg border-2 border-dashed border-gray-600 flex items-center justify-center cursor-pointer" data-testid="poscan-upload-photo-label">
                      {uploadingImg ? <Loader2 className="w-5 h-5 animate-spin text-gray-400" /> : <Camera className="w-5 h-5 text-gray-400" />}
                      <input type="file" accept="image/*" capture="environment" className="hidden"
                        onChange={(e) => { uploadGrnImage(e.target.files?.[0]); e.target.value = ''; }} data-testid="poscan-upload-photo-input" />
                    </label>
                  </div>
                </div>
                <button onClick={submitGrn} disabled={acting}
                  className="w-full py-4 rounded-xl bg-green-600 font-bold text-base flex items-center justify-center gap-2" data-testid="poscan-submit-grn-btn">
                  {acting ? <Loader2 className="w-5 h-5 animate-spin" /> : <ClipboardCheck className="w-5 h-5" />} Receive Goods (Submit GRN)
                </button>
              </>
            ) : (
              <p className="text-sm text-gray-400 text-center" data-testid="poscan-no-actions">
                {allReceived ? '✓ All units received for this PO.' : `No receiving actions available for status "${String(status || '').replace(/_/g, ' ')}".`}
              </p>
            )}
            <button onClick={() => navigate(`/po-labels/${poId}`)}
              className="w-full py-3 rounded-xl border-2 border-amber-500/50 text-amber-400 font-semibold flex items-center justify-center gap-2" data-testid="poscan-staff-print-labels-btn">
              <Printer className="w-5 h-5" /> Print Labels
            </button>
            <button onClick={() => navigate(`/purchase-orders/${poId}/grn`)}
              className="w-full py-2 text-xs text-gray-400 underline" data-testid="poscan-full-grn-link">
              Open full GRN screen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
